import React, { useState, useEffect } from 'react';
import {
  Search,
  Calendar,
  Clock,
  User,
  Phone,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowUpRight,
  MapPin,
  RefreshCw,
  RotateCcw,
  Users,
  X,
  Bike,
  IndianRupee
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { formatBookingId, format24HourTime } from '../../context/DashboardContext';

const EventBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    date: '',
    activity: '',
    search: ''
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchBookings();

    // Auto-refresh in background every 15 seconds so browser refresh is never needed
    const interval = setInterval(() => {
      fetchBookings(true);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const getToken = () => {
    return localStorage.getItem('token_partner_activities');
  };

  const fetchBookings = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const token = getToken();
      if (!token) return;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const businessId = payload._id || payload.business_id;

      let myTasks = [];
      try {
        const tasksResponse = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/task`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        myTasks = tasksResponse.data || [];
      } catch (err) {
        console.error('Error fetching tasks:', err);
      }

      let allBookings = [];
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/reservation/business/${businessId}/Task`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data && response.data.success) {
          if (Array.isArray(response.data.active) || Array.isArray(response.data.inactive)) {
            allBookings = [...(response.data.active || []), ...(response.data.inactive || [])];
          } else if (Array.isArray(response.data.reservations)) {
            allBookings = response.data.reservations;
          } else if (Array.isArray(response.data.data)) {
            allBookings = response.data.data;
          } else if (Array.isArray(response.data)) {
            allBookings = response.data;
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) console.error(error);
      }

      const transformedBookings = allBookings.map(booking => {
        const correspondingTask = myTasks.find(task => 
          task._id === booking.business_id?._id || task._id === booking.business_id
        );
        const activityName = booking.business_id?.name ||
          booking.business_id?.superadmin_activity_id?.name ||
          booking.businessDetails?.activity_name ||
          (booking.businessDetails?.business_name && booking.businessDetails?.business_name !== 'Trrip activity' ? booking.businessDetails?.business_name : null) ||
          correspondingTask?.superadmin_activity_id?.name ||
          correspondingTask?.activity_name ||
          'Activity Experience';

        const cityStr = booking.businessDetails?.city || booking.business_id?.city || correspondingTask?.city || 'Srinagar';

        return {
          ...booking,
          businessDetails: {
            ...booking.businessDetails,
            business_name: activityName,
            activity_name: activityName,
            city: cityStr
          }
        };
      });

      transformedBookings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setBookings(transformedBookings);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      setUpdating(bookingId);
      const token = getToken();

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/reservation/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: newStatus } : b));
      toast.success(`Booking ${newStatus.toLowerCase()} successfully`);
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const searchTerm = filters.search.toLowerCase();
    const formattedId = formatBookingId(booking.booking_id || booking._id);

    const matchesSearch = !searchTerm ||
      booking.businessDetails?.business_name?.toLowerCase().includes(searchTerm) ||
      formattedId.includes(searchTerm) ||
      booking.booking_id?.name?.toLowerCase().includes(searchTerm) ||
      booking.booking_id?.phone_number?.includes(searchTerm);

    const matchesStatus = !filters.status || booking.status === filters.status;
    const matchesActivity = !filters.activity || booking.businessDetails?.business_name === filters.activity;
    const matchesDate = !filters.date || (booking.date && booking.date.includes(filters.date));

    return matchesSearch && matchesStatus && matchesActivity && matchesDate;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Pending': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Cancelled': return 'bg-rose-50 text-rose-600 border-rose-100';
      case 'Completed': return 'bg-[#067978]/10 text-[#067978] border-[#067978]/20';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'Pending').length,
    confirmed: bookings.filter(b => b.status === 'Confirmed').length,
    completed: bookings.filter(b => b.status === 'Completed').length
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <div className="w-10 h-10 border-4 border-[#067978] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-500 font-extrabold uppercase tracking-widest text-[10px] animate-pulse">Syncing reservations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 animate-in fade-in duration-700 pb-10">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[#067978] tracking-tight uppercase">Reservation Desk</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Manage your guest influx and pending approvals.</p>
        </div>
        <button 
          onClick={fetchBookings} 
          className="px-4 py-2 bg-white border border-slate-100 rounded-xl text-slate-600 font-extrabold text-xs shadow-sm hover:shadow-md transition-all flex items-center gap-2 active:scale-95 w-fit"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* Compact Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {[
          { label: 'Total Volume', value: stats.total, icon: Calendar, color: 'teal' },
          { label: 'Pending Desk', value: stats.pending, icon: AlertCircle, color: 'amber' },
          { label: 'Confirmed', value: stats.confirmed, icon: CheckCircle2, color: 'emerald' },
          { label: 'Completed', value: stats.completed, icon: ArrowUpRight, color: 'indigo' }
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{stat.label}</span>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  stat.color === 'teal' ? 'bg-teal-50 text-[#067978]' :
                  stat.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                  stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                  'bg-indigo-50 text-indigo-600'
                }`}>
                  <Icon size={16} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-black text-slate-800 leading-none">{stat.value.toLocaleString()}</p>
            </div>
          );
        })}
      </div>

      {/* Compact Filter Bar */}
      <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#067978] transition-colors" size={15} />
            <input
              type="text"
              placeholder="Search by ID or Experience..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#067978]/20 outline-none"
            />
          </div>

          <input 
            type="date" 
            value={filters.date} 
            onChange={(e) => setFilters(v => ({ ...v, date: e.target.value }))} 
            className="px-3 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#067978]/20 outline-none cursor-pointer" 
          />

          <select 
            value={filters.status} 
            onChange={(e) => setFilters(v => ({ ...v, status: e.target.value }))} 
            className="px-3 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#067978]/20 outline-none cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          {(filters.search || filters.date || filters.status) && (
            <button 
              onClick={() => setFilters({ status: '', date: '', activity: '', search: '' })} 
              className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all flex items-center gap-1 text-[11px] font-bold"
              title="Reset Filters"
            >
              <RotateCcw size={14} />
              <span>Reset</span>
            </button>
          )}
        </div>
      </div>

      {/* Compact Table View Container */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {filteredBookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Booking ID</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Experience</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date & 24h Slot</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Created At</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Group</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Final Bill</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Your Share</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBookings.map((booking) => {
                  const isUnconfirmed = !['confirmed', 'completed'].includes((booking.status || '').toLowerCase());
                  const finalBill = booking.final_bill_amount || booking.advance_Amt || 0;
                  const shareAmount = booking.partnerShare ?? 0;

                  // Strictly format Booking ID: 5-char lowercase (a-z, 0-9)
                  const formattedId = formatBookingId(booking.booking_id || booking._id);

                  // Strictly format Time: 24-hour format (HH:mm)
                  const time24 = format24HourTime(booking.bookedTime || booking.time || booking.date);

                  const expName = booking.businessDetails?.activity_name ||
                    booking.businessDetails?.business_name ||
                    booking.business_id?.name ||
                    booking.business_id?.superadmin_activity_id?.name ||
                    'Activity Experience';

                  const cityStr = booking.businessDetails?.city ||
                    booking.business_id?.city ||
                    'Srinagar';

                  return (
                    <tr 
                      key={booking._id} 
                      className="hover:bg-slate-50/80 transition-colors text-xs group cursor-pointer"
                      onClick={() => { setSelectedBooking(booking); setShowModal(true); }}
                    >
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-600 lowercase">{formattedId}</td>
                      <td className="py-3.5 px-4">
                        <div>
                          <p className="font-extrabold text-slate-900 group-hover:text-[#067978] transition-colors text-xs">{expName}</p>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                            <MapPin size={10} className="text-[#067978]" /> {cityStr}
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-700">{new Date(booking.date).toLocaleDateString()}</span>
                          <span className="text-[10px] font-bold text-[#067978] flex items-center gap-1 mt-0.5">
                            <Clock size={10} />
                            {time24}
                          </span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-[11px] font-bold text-slate-500">
                          {booking.createdAt ? new Date(booking.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-slate-50 rounded-full border border-slate-100 text-slate-600 font-extrabold text-[11px]">
                          <Users size={12} className="text-[#067978]" /> {booking.totalMembers || 1}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-black text-slate-800 text-xs">
                        ₹{Number(finalBill).toLocaleString('en-IN')}
                      </td>
                      <td className="py-3.5 px-4 font-black text-emerald-600 text-xs">
                        ₹{Number(shareAmount).toLocaleString('en-IN')}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        {isUnconfirmed ? (
                          <div className="flex justify-end items-center gap-1.5">
                            <button
                              onClick={() => updateBookingStatus(booking._id, 'Confirmed')}
                              disabled={updating === booking._id}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold rounded-lg shadow-sm transition-all flex items-center gap-1 active:scale-95 disabled:opacity-50"
                            >
                              <CheckCircle2 size={12} />
                              Confirm
                            </button>
                            <button
                              onClick={() => updateBookingStatus(booking._id, 'Cancelled')}
                              disabled={updating === booking._id}
                              className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 text-[10px] font-extrabold rounded-lg transition-all flex items-center gap-1 active:scale-95 disabled:opacity-50"
                            >
                              <XCircle size={12} />
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400 font-bold">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="p-3 bg-slate-50 rounded-2xl text-slate-300 mb-3"><Calendar size={24} /></div>
            <h3 className="text-sm font-black text-slate-700 uppercase tracking-tight">No Reservations</h3>
            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider max-w-[240px]">All activity experience bookings will appear here once guests book through your portal.</p>
          </div>
        )}
      </div>

      {/* Booking Details Modal (Exact Image 1 Design) */}
      {showModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative border border-slate-100 p-6 md:p-7 animate-in fade-in zoom-in-95 duration-200">
            {/* Top Right Close Button */}
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100/80 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all cursor-pointer"
            >
              <X size={16} />
            </button>

            {/* Top Left Icon + Activity Title Block */}
            <div className="flex items-start gap-4 mb-6 pr-8">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#067978] flex items-center justify-center flex-shrink-0">
                <Bike className="w-7 h-7 text-[#067978]" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest block">
                  ACTIVITY
                </span>
                <h2 className="text-xl font-bold text-slate-900 leading-snug mt-0.5">
                  {selectedBooking.business_id?.name || selectedBooking.business_id?.superadmin_activity_id?.name || selectedBooking.businessDetails?.activity_name || (selectedBooking.businessDetails?.business_name && selectedBooking.businessDetails?.business_name !== 'Trrip activity' ? selectedBooking.businessDetails?.business_name : null) || selectedBooking.package_name || 'Activity Experience'}
                </h2>
                <p className="flex items-center gap-1 text-[11px] text-slate-400 font-normal mt-1">
                  <MapPin size={10} className="text-emerald-600" />
                  <span>Srinagar, Jammu & Kashmir</span>
                </p>
              </div>
            </div>

            {/* Guest & Status Row */}
            <div className="grid grid-cols-2 gap-4 py-5 border-t border-b border-slate-100 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-emerald-50 text-[#067978] font-bold text-base flex items-center justify-center flex-shrink-0">
                  {(selectedBooking.guest_name || selectedBooking.guestName || selectedBooking.booking_id?.name || 'M').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">GUEST</p>
                  <p className="text-base font-bold text-slate-900 mt-0.5">{selectedBooking.guest_name || selectedBooking.guestName || selectedBooking.booking_id?.name || 'Guest'}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">STATUS</p>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {selectedBooking.status || 'Completed'}
                </span>
              </div>
            </div>

            {/* Details Grid with Vertical Separator */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-6 mb-6 text-xs">
              {/* Left Column */}
              <div className="space-y-5 border-r border-slate-100 pr-4">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Calendar size={14} className="text-slate-400" /> DATE
                  </p>
                  <p className="text-xs font-semibold text-slate-800">
                    {new Date(selectedBooking.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Clock size={14} className="text-slate-400" /> CREATED AT
                  </p>
                  <p className="text-xs font-medium text-slate-700">
                    {selectedBooking.createdAt ? new Date(selectedBooking.createdAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <IndianRupee size={14} className="text-slate-400" /> BILL AMOUNT
                  </p>
                  <p className="text-base font-bold text-slate-800">
                    ₹{Number(selectedBooking.final_bill_amount || selectedBooking.advance_Amt || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5 pl-2">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Clock size={14} className="text-slate-400" /> TIME
                  </p>
                  <p className="text-xs font-semibold text-slate-800">
                    {format24HourTime(selectedBooking.bookedTime || selectedBooking.time || selectedBooking.date)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Users size={14} className="text-slate-400" /> TOTAL MEMBERS
                  </p>
                  <p className="text-xs font-medium text-slate-700">
                    {selectedBooking.totalMembers || 1}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <IndianRupee size={14} className="text-slate-400" /> YOUR SHARE
                  </p>
                  <p className="text-base font-bold text-emerald-600">
                    ₹{Number(selectedBooking.partnerShare ?? 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Action */}
            <div className="flex justify-end pt-2">
              <button 
                onClick={() => setShowModal(false)}
                className="px-7 py-2.5 bg-[#067978] hover:bg-[#056463] text-white text-xs font-bold rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventBookings;
